import React, { useCallback, useEffect, useMemo, useRef } from "react";
import ScreenCard, { ScreenCardHandle } from "../ScreenCard";
import { CreateMissionScreensContext } from "../../contexts/CreateMissionManager";
import { useI18n } from "../../hooks/i18n-hooks";
import MissionFees, {
    MissionFeeResult,
    validateFeeData,
} from "../../components/editors/MissionFees";
import { newMissionState } from "../../atoms/core";
import { useRecoilState } from "recoil";
import { FeeType, MissionFee } from "../../__generated__/graphql";
import { Platform } from "react-native";
import { useKeyboardVisible } from "../../hooks/keyboard";
import { useBubbleStepOrigin } from "../../hooks/use-bubble-step-origin";
import { Bubble } from "../../components/Bubble";

const CreateMissionFees = () => {
    const i18n = useI18n();
    const { validateScreen } = React.useContext(CreateMissionScreensContext);
    const [mission, setMission] = useRecoilState(newMissionState);

    const setFees = useCallback(
        (fees: Array<MissionFeeResult>) => {
            setMission((oldMission) => {
                const cctFees =
                    oldMission.currentContract?.fees?.filter((fee) => fee?.type === FeeType.Cct) ||
                    [];
                return {
                    ...oldMission,
                    currentContract: {
                        ...oldMission.currentContract,
                        fees: [...fees, ...cctFees],
                    },
                };
            });
        },
        [setMission],
    );

    const fees = useMemo(() => {
        return (mission.currentContract?.fees as Array<MissionFee>) || [];
    }, [mission.currentContract?.fees]);

    const screenCardRef = useRef<ScreenCardHandle>(null);

    const isKeyboardVisible = useKeyboardVisible();

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const otherIsEnabled = useMemo(() => {
        return fees.some((fee) => fee?.type === FeeType.Other);
    }, [fees]);

    useEffect(() => {
        // iOS automatically scroll to correctly see fields when it is (auto)focused. Android needs this trick to scroll to the bottom after enabling the "Other" item.
        if (otherIsEnabled && isKeyboardVisible && Platform.OS === "android") {
            setTimeout(() => {
                screenCardRef.current?.scrollContentToEnd();
            }, 10);
        }
    }, [otherIsEnabled, isKeyboardVisible]);

    const isValid = useMemo(() => {
        return fees.reduce((acc, fee) => {
            if (!acc) {
                return false;
            }
            return validateFeeData(fee);
        }, true);
    }, [fees]);

    const validate = React.useCallback(() => {
        void validateScreen("CreateMissionFees");
    }, [validateScreen]);

    const origin = useBubbleStepOrigin("CreateMissionFees");

    return (
        <ScreenCard
            ref={screenCardRef}
            buttons={[
                {
                    title: i18n.translate("screens.user.create_mission_fees.next_title"),
                    onPress: validate,
                    isValid: isValid,
                },
            ]}
        >
            <Bubble
                title={i18n.t("screens.user.create_mission_fees.title")}
                origin={origin}
                bubbleStyle={{ paddingBottom: 0 }}
            >
                <MissionFees
                    fees={fees}
                    setFees={setFees}
                    repetition={mission.currentContract?.workingTimeClassicSpec?.repetition}
                />
            </Bubble>
        </ScreenCard>
    );
};

export default CreateMissionFees;
